import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronUp, ChevronDown, MapPin, Users, Link, ClipboardList, DollarSign, ArrowRight, X, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const STEPS = [
  { icon: MapPin, title: 'Add your first location', key: 'has_location', path: '/admin/locations', color: '#2563EB' },
  { icon: Users, title: 'Add a team member', key: 'has_team_member', path: '/admin/users', color: '#7C3AED' },
  { icon: Link, title: 'Configure dropdowns (Sales Staff, EOD Staff)', key: 'has_form_token', path: '/admin/dropdowns', color: '#D97706', description: 'Setup Sales Staff for "Record Sale" and EOD Staff for "EOD Summary"' },
  { icon: ClipboardList, title: 'Submit your first EOD', key: 'has_eod', path: '/eod/new', color: '#059669' },
  { icon: DollarSign, title: 'Record your first sale', key: 'has_sale', path: '/sales/new', color: '#DC2626' },
];

export default function OnboardingModal({ user }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [checks, setChecks] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    if (!user || user.role === 'super_admin') return;
    
    fetchOnboarding();
    
    // Only start auto-refresh if NOT in cooldown
    const minKey = `onboarding_minimized_${user?.id || user?._id}`;
    const minimizedTime = localStorage.getItem(minKey);
    
    if (!minimizedTime) {
      // Not minimized - start 5-second refresh
      const interval = setInterval(() => {
        fetchOnboarding();
      }, 5000);
      setIntervalId(interval);
      
      return () => clearInterval(interval);
    }
    // If minimized, don't start interval at all
  }, [user]);

  const fetchOnboarding = async () => {
    try {
      // FIRST: Check if user wants it closed (30-min cooldown)
      const minKey = `onboarding_minimized_${user?.id || user?._id}`;
      const minimizedTime = localStorage.getItem(minKey);
      
      if (minimizedTime) {
        const thirtyMinutes = 30 * 60 * 1000;
        const elapsed = Date.now() - parseInt(minimizedTime);
        
        if (elapsed < thirtyMinutes) {
          // User wants it closed - respect that
          if (open) setOpen(false);  // Force close if somehow open
          setLoaded(true);
          return; // EXIT - don't check anything else
        } else {
          // Cooldown expired
          localStorage.removeItem(minKey);
        }
      }
      
      // Now check actual onboarding status
      const res = await axios.get(`${API}/settings/onboarding`);
      setChecks(res.data.checks || {});
      const allDone = Object.values(res.data.checks || {}).every(Boolean);
      
      if (!allDone && !res.data.onboarding_completed) {
        setOpen(true);
        setExpanded(true);
      }
    } catch {}
    setLoaded(true);
  };

  const completedCount = STEPS.filter(s => checks[s.key]).length;
  const allDone = completedCount === STEPS.length;

  const minimize = () => {
    // Single source of truth for closing the widget:
    // 1) record cooldown (30 min) so fetchOnboarding won't re-open it
    // 2) clear the polling interval so it stops re-expanding every 5s
    // 3) collapse + hide
    const key = `onboarding_minimized_${user?.id || user?._id}`;
    localStorage.setItem(key, Date.now().toString());
    setOpen(false);
    setExpanded(false);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  };

  const handleDismiss = () => {
    if (allDone) {
      const key = `onboarding_dismissed_${user?.id || user?._id}`;
      localStorage.setItem(key, '1');
      axios.put(`${API}/settings/onboarding`, { onboarding_completed: true }).catch(() => {});
      setOpen(false);
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(null);
      }
    } else {
      minimize();
    }
  };

  const handleToggle = () => {
    if (expanded) {
      // Collapsing the expanded panel → treat as minimize (cooldown + clear interval)
      minimize();
    } else {
      // Expanding from pill — user explicitly wants to see it
      setExpanded(true);
    }
  };

  const handleStepClick = (path) => {
    // Don't auto-collapse when clicking steps
    navigate(path);
  };

  if (!open || !loaded) return null;

  return (
    <div className="fixed bottom-5 right-5 z-50" style={{ maxWidth: 320 }}>
      <AnimatePresence>
        {expanded ? (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="rounded-2xl overflow-hidden"
            style={{
              background: '#0A0F1E',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.2)'
            }}
          >
            {/* Header */}
            <div className="px-4 py-3.5" style={{ background: 'rgba(37,99,235,0.15)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#1D4ED8' }}>
                    <Zap size={13} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold font-heading">Getting Started</p>
                    <p className="text-xs" style={{ color: 'rgba(148,163,184,0.8)' }}>{completedCount}/{STEPS.length} steps complete</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={minimize} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Hide for 30 minutes" data-testid="onboarding-minimize-btn">
                    <ChevronDown size={14} className="text-slate-400" />
                  </button>
                  <button onClick={minimize} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Hide for 30 minutes" data-testid="onboarding-close-btn">
                    <X size={14} className="text-slate-400" />
                  </button>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-2.5 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }}>
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${(completedCount / STEPS.length) * 100}%`, background: '#2563EB' }} />
              </div>
            </div>

            {/* Steps */}
            <div className="p-3 space-y-1.5">
              {STEPS.map((step) => {
                const done = checks[step.key];
                const Icon = step.icon;
                return (
                  <button
                    key={step.key}
                    onClick={() => !done && handleStepClick(step.path)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors ${
                      done ? 'opacity-50 cursor-default' : 'hover:bg-white/5 cursor-pointer'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      done ? 'bg-green-900/40' : ''
                    }`} style={done ? {} : { background: step.color + '22' }}>
                      {done
                        ? <CheckCircle size={14} className="text-green-500" />
                        : <Icon size={14} style={{ color: step.color }} />}
                    </div>
                    <span className={`text-sm flex-1 ${ done ? 'line-through text-slate-600' : 'text-slate-200' }`}>
                      {step.title}
                    </span>
                    {!done && <ArrowRight size={12} className="text-slate-600 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
              {allDone ? (
                <button onClick={handleDismiss} className="w-full h-8 rounded-lg text-sm font-semibold text-white"
                  style={{ background: '#16A34A' }}>
                  🎉 All done — dismiss
                </button>
              ) : (
                <button onClick={minimize} className="text-xs w-full text-center"
                  style={{ color: 'rgba(100,116,139,0.7)' }} data-testid="onboarding-footer-minimize-btn">
                  Minimize
                </button>
              )}
            </div>
          </motion.div>
        ) : (
          // Collapsed pill
          <motion.button
            key="collapsed"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={handleToggle}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl transition-all hover:scale-105 active:scale-95"
            style={{
              background: '#0A0F1E',
              border: '1px solid rgba(37,99,235,0.4)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
          >
            <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#1D4ED8' }}>
              <Zap size={11} className="text-white" />
            </div>
            <span className="text-white text-sm font-medium">Getting started</span>
            <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
              style={{ background: 'rgba(37,99,235,0.2)', color: '#60A5FA' }}>
              {completedCount}/{STEPS.length}
            </span>
            <ChevronUp size={13} className="text-slate-500" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
